import React, { useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardActions from "@mui/material/CardActions";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CardMedia from "@mui/material/CardMedia";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import { getProductsList } from "../../../../../services/Admin/MenuApi";
import { updateProduct } from "../../../../../services/Admin/MenuProductsApi";
import { resolveImageUrl } from "../../../../../services/indexMediaBaseUrl";
import ItemDetailsDialog from "./ItemDetailsDialog";
import AddProductDialoge from "./AddProductDialoge";
import EditeItemDialog from "./EditeItemDialog";
import { useAuth } from "../../../../../Context/AuthContext";

const UNCATEGORIZED_ID = "uncategorized";
const UNCATEGORIZED_NAME = "بدون فئة";

const isDeletedProduct = (product) => Number(product?.status) === 0;

const getProductCategoryId = (product) =>
	product?.category_id ?? product?.category?.id ?? UNCATEGORIZED_ID;

const ItemsTapMain = () => {
	const [products, setProducts] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [selectedId, setSelectedId] = useState(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [addDialogOpen, setAddDialogOpen] = useState(false);
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [editId, setEditId] = useState(null);
	const [searchText, setSearchText] = useState("");
	const [selectedCategory, setSelectedCategory] = useState("all");
	const [activeTab, setActiveTab] = useState("active");
	const [deletingId, setDeletingId] = useState(null);
	const { hasPermission, userType, userRole } = useAuth();
	const canViewDeletedTab = userType === "admin" && userRole === "admin";

	const fetchProducts = async () => {
		setLoading(true);
		setError("");
		try {
			const res = await getProductsList();
			setProducts(res?.data || []);
		} catch (err) {
			setError(err?.message || "فشل تحميل الأصناف");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchProducts();
	}, []);

	useEffect(() => {
		if (!canViewDeletedTab && activeTab === "deleted") {
			setActiveTab("active");
		}
	}, [activeTab, canViewDeletedTab]);

	const categories = useMemo(() => {
		const map = new Map();

		products.forEach((product) => {
			const id = getProductCategoryId(product);
			const name = product?.category?.name || UNCATEGORIZED_NAME;
			if (!map.has(id)) {
				map.set(id, { id, name });
			}
		});

		const list = Array.from(map.values());
		list.sort((a, b) => {
			if (a.id === UNCATEGORIZED_ID) return 1;
			if (b.id === UNCATEGORIZED_ID) return -1;
			return String(a.name).localeCompare(String(b.name), "ar");
		});

		return list;
	}, [products]);

	const activeProductsCount = useMemo(
		() => products.filter((product) => !isDeletedProduct(product)).length,
		[products]
	);

	const deletedProductsCount = useMemo(
		() => products.filter(isDeletedProduct).length,
		[products]
	);

	const filteredProducts = useMemo(() => {
		const q = searchText.trim().toLowerCase();

		return products.filter((product) => {
			const isDeleted = isDeletedProduct(product);
			if (activeTab === "active" && isDeleted) return false;
			if (activeTab === "deleted" && (!canViewDeletedTab || !isDeleted)) return false;

			const categoryId = getProductCategoryId(product);
			const matchesCategory =
				selectedCategory === "all" || String(categoryId) === String(selectedCategory);

			if (!matchesCategory) return false;
			if (!q) return true;

			const fields = [
				product?.name,
				product?.name_en,
				product?.code,
				product?.description,
				product?.description_en,
				product?.category?.name,
				product?.category?.name_en,
			];

			return fields.some((field) =>
				String(field || "").toLowerCase().includes(q)
			);
		});
	}, [products, searchText, selectedCategory, activeTab, canViewDeletedTab]);

	const groupedProducts = useMemo(() => {
		const groupsMap = new Map();

		filteredProducts.forEach((product) => {
			const categoryId = getProductCategoryId(product);
			const categoryName = product?.category?.name || UNCATEGORIZED_NAME;

			if (!groupsMap.has(categoryId)) {
				groupsMap.set(categoryId, {
					id: categoryId,
					name: categoryName,
					items: [],
				});
			}

			groupsMap.get(categoryId).items.push(product);
		});

		const groups = Array.from(groupsMap.values());
		groups.sort((a, b) => {
			if (a.id === UNCATEGORIZED_ID) return 1;
			if (b.id === UNCATEGORIZED_ID) return -1;
			return String(a.name).localeCompare(String(b.name), "ar");
		});

		return groups;
	}, [filteredProducts]);

	const handleOpenDetails = (productId) => {
		if (hasPermission("view")) {
			setSelectedId(productId);
			setDialogOpen(true);
			return;
		}

		alert("ليس لديك الصلاحية لرؤية تفاصيل هذا الصنف");
	};

	const handleSoftDeleteProduct = async (product) => {
		if (!product?.id || deletingId) return;

		const confirmed = window.confirm(`هل تريد حذف الصنف "${product?.name || "-"}"؟`);
		if (!confirmed) return;

		setDeletingId(product.id);
		setError("");

		try {
			await updateProduct({
				...product,
				id: product.id,
				name: product.name || "",
				name_en: product.name_en || "",
				description: product.description || "",
				description_en: product.description_en || "",
				price: product.price || "",
				category_id: getProductCategoryId(product) === UNCATEGORIZED_ID ? "" : getProductCategoryId(product),
				image: product.image,
				prepare_time: product.prepare_time || "",
				status: 0,
			});

			setProducts((prev) =>
				prev.map((item) =>
					item.id === product.id ? { ...item, status: 0 } : item
				)
			);
			fetchProducts();
		} catch (err) {
			setError(err?.message || "فشل حذف الصنف");
		} finally {
			setDeletingId(null);
		}
	};

	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box>
			{error && (
				<Box sx={{ color: "error.main", p: 2, mb: 2, bgcolor: "#fff5f5", borderRadius: 1 }}>
					{String(error)}
				</Box>
			)}

			{hasPermission("add") && (
				<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
					<Button variant="contained" onClick={() => setAddDialogOpen(true)}>
						إضافة صنف
					</Button>
				</Box>
			)}

			<Box sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
				<Tabs
					value={activeTab}
					onChange={(e, value) => setActiveTab(value)}
					variant="scrollable"
					scrollButtons="auto"
				>
					<Tab value="active" label={`الأصناف (${activeProductsCount})`} />
					{canViewDeletedTab && (
						<Tab value="deleted" label={`المحذوفات (${deletedProductsCount})`} />
					)}
				</Tabs>
			</Box>

			<Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
				<TextField
					size="small"
					placeholder="ابحث باسم الصنف أو الكود أو الفئة"
					value={searchText}
					onChange={(e) => setSearchText(e.target.value)}
					sx={{ minWidth: 260, flex: 1 }}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon fontSize="small" />
							</InputAdornment>
						),
					}}
				/>

				<FormControl size="small" sx={{ minWidth: 220 }}>
					<InputLabel id="category-filter-label">الفئة</InputLabel>
					<Select
						labelId="category-filter-label"
						label="الفئة"
						value={selectedCategory}
						onChange={(e) => setSelectedCategory(e.target.value)}
					>
						<MenuItem value="all">كل الفئات</MenuItem>
						{categories.map((cat) => (
							<MenuItem key={cat.id} value={cat.id}>
								{cat.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Box>

			{groupedProducts.length === 0 && (
				<Box sx={{ p: 3, borderRadius: 1, backgroundColor: "grey.100", color: "text.secondary" }}>
					لا توجد نتائج مطابقة للبحث أو الفلتر.
				</Box>
			)}

			{groupedProducts.map((group) => (
				<Box key={group.id} sx={{ mb: 3 }}>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							mb: 1.5,
							p: 1,
							borderRadius: 1,
							backgroundColor: "grey.100",
						}}
					>
						<Typography variant="h6" fontWeight="bold">
							{group.name}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{group.items.length} منتج
						</Typography>
					</Box>

					<Grid container spacing={2}>
						{group.items.map((product) => {
							const isDeleted = isDeletedProduct(product);
							const isDeleting = deletingId === product.id;

							return (
								<Grid item key={product.id} xs={12} sm={6} md={4} lg={3}>
									<Card
										sx={{
											border: "1px solid",
											borderColor: isDeleted ? "error.light" : "divider",
											backgroundColor: isDeleted ? "#fff5f5" : "background.paper",
											opacity: isDeleted ? 0.85 : 1,
										}}
									>
										<CardActionArea onClick={() => handleOpenDetails(product.id)}>
											<CardMedia
												component="img"
												height="140"
												image={resolveImageUrl(product.image)}
												alt={product.name}
											/>
											<CardContent>
												<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
													<Typography variant="subtitle1" fontWeight="bold">
														{product.name}
													</Typography>
													<Chip
														size="small"
														label={isDeleted ? "محذوف" : "متاح"}
														color={isDeleted ? "error" : "success"}
														sx={{ fontWeight: 700 }}
													/>
												</Box>
												<Typography variant="body2" color="text.secondary">
													{product.category?.name || UNCATEGORIZED_NAME}
												</Typography>
												<Typography variant="body2" sx={{ mt: 1 }}>
													السعر: {product.price}
												</Typography>
												<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
													وقت التحضير: {product.prepare_time ?? 0} دقيقة
												</Typography>
											</CardContent>
										</CardActionArea>

										<CardActions disableSpacing>
											{hasPermission("edit") && (
												<IconButton
													onClick={() => {
														setEditId(product.id);
														setEditDialogOpen(true);
													}}
													disabled={isDeleting}
													aria-label="edit"
												>
													<EditIcon />
												</IconButton>
											)}

											{!isDeleted && (hasPermission("delete") || hasPermission("edit")) && (
												<IconButton
													onClick={() => handleSoftDeleteProduct(product)}
													disabled={isDeleting}
													aria-label="delete"
													color="error"
												>
													{isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
												</IconButton>
											)}
										</CardActions>
									</Card>
								</Grid>
							);
						})}
					</Grid>
				</Box>
			))}

			<ItemDetailsDialog open={dialogOpen} onClose={() => setDialogOpen(false)} productId={selectedId} />
			<AddProductDialoge
				open={addDialogOpen}
				onClose={() => setAddDialogOpen(false)}
				onAdded={() => {
					fetchProducts();
				}}
			/>
			<EditeItemDialog
				open={editDialogOpen}
				onClose={() => setEditDialogOpen(false)}
				productId={editId}
				onUpdated={() => {
					fetchProducts();
					setEditDialogOpen(false);
				}}
			/>
		</Box>
	);
};

export default ItemsTapMain;
